<template>
  <div class="container">
    <FloatingDots v-if="gettingData"></FloatingDots>
    <div class="form-wrapper">
      <div class="header">
        <h1>Student Information</h1>
        <PermissionsWrapper
          :permissionIds="[Permissions.Draws]"
          :permissionLevel="PermissionLevels.Update"
        >
          <button
            class="submit-button col-12"
            v-if="mode === FormModes.view && !gettingData && canUpdate"
            @click="changeMode('edit')"
          >
            Edit
          </button>
          <button
            class="submit-button col-12"
            v-if="mode === FormModes.edit && !gettingData && canUpdate"
            @click="handleCancelButtonClick"
          >
            Cancel
          </button>
        </PermissionsWrapper>
      </div>
      <form class="studentInformationForm draws-tab" v-show="!gettingData">
        <fieldset :disabled="mode === FormModes.view">
          <VeeField v-show="false" name="person" v-model="person"></VeeField>
          <div class="xform-section">
            <div class="xfields xfield-group">
              <base-form-field
                :schema="schema.firstName"
                v-model="person.firstName"
                @update:model-value="handleFieldChange('firstName', $event)"
              />
              <base-form-field
                :schema="schema.lastName"
                v-model="person.lastName"
                @update:model-value="handleFieldChange('lastName', $event)"
              />
              <base-form-field
                :schema="schema.dateOfBirth"
                v-model="person.dateOfBirth"
                @update:model-value="handleFieldChange('dateOfBirth', $event)"
              />
            </div>
            <CollapsibleSection
              :isVisible="showAdditionalInfo || mode === FormModes.edit"
            >
              <div class="xfields xfield-group">
                <base-form-field
                  :schema="schema.citizenshipStatusId"
                  v-model.number="person.citizenshipStatusId"
                  :options="citizenshipStatusOptions"
                  @update:model-value="
                    handleFieldChange('citizenshipStatusId', $event)
                  "
                />
                <NationalIdNumberInput
                  :disabled="savingChanges || mode === FormModes.view"
                  v-model="person.nationalIdNumber"
                  :permissionIds="[Permissions.ApplicantSSN]"
                  :permissionLevel="PermissionLevels.Update"
                  :fieldErrors="
                    errors.nationalIdNumber && mode === FormModes.edit
                      ? [errors.nationalIdNumber]
                      : []
                  "
                  :schema="schema.nationalIdNumber"
                  :nationalIdNumberTypeId="person.nationalIdNumberTypeId"
                  @update:itinIndicator="
                    handleFieldChange('itinIndicator', $event)
                  "
                  @update:model-value="
                    handleFieldChange('nationalIdNumber', $event)
                  "
                />
                <AddressInput
                  ref="addressInputRef"
                  :initialValues="student?.address || {}"
                  :disabled="mode === FormModes.view"
                  :mode="mode"
                  header="Student Address"
                  :showHeader="false"
                  :entityTypeId="ConsentServiceEntityTypeIds.Draw"
                  :showUnitType="showUnitType"
                />
              </div>
            </CollapsibleSection>

            <div style="display: none">
              <ParentAttestationConsent
                ref="parentAttestationConsentRef"
                v-model="person.parentConsent"
                :disabled="savingChanges || mode !== FormModes.add"
                :entityId="draw?.id || 0"
                :entityTypeId="ConsentServiceEntityTypeIds.Application"
                :details="draw?.parentAttestation"
                :mode="mode"
                :required="mode === FormModes.add"
                @update:modelValue="person.parentConsent = $event"
              />
            </div>
          </div>
        </fieldset>
        <button
          v-show="mode === FormModes.edit"
          class="submit"
          type="submit"
          @click.prevent="submit"
          :disabled="savingChanges"
        >
          Submit
        </button>
      </form>

      <div
        v-show="!gettingData && mode === FormModes.view"
        class="toggle"
        @click="showAdditionalInfo = !showAdditionalInfo"
      >
        {{ showAdditionalInfo ? "Hide" : "View" }} Additional Information
        <span
          :class="[
            showAdditionalInfo ? 'icon-up-open-big' : 'icon-down-open-big',
          ]"
        ></span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import { $api } from "@/services/api1";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";
import functions from "@/use/functions";
import { useStudentFormFields } from "@/composables/student/useStudentFormFields";
import { Permissions, PermissionLevels } from "@/models/permissions";
import { FormModes } from "@/types/form";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { Field as VeeField } from "vee-validate";
import BaseFormField from "@/components/form-fields/BaseFormField.vue";
import AddressInput from "@/components/v2/application/forms/AddressInput.vue";
import CollapsibleSection from "@/components/v2/transitions/CollapsibleSection.vue";
import NationalIdNumberInput from "@/components/inputs/NationalIdNumberInput.vue";
import FloatingDots from "@/components/loaders/FloatingDots.vue";
import ParentAttestationConsent from "@/components/v2/consents/ParentAttestationConsent.vue";
import { AddressTypeIds } from "@/types/address";
import { getOptionLabel } from "@/composables/application/useApplicationForms";
import type { ApplicantAddress } from "@/types/applicant";
import type { LoanDetails } from "@/models/loans";
import type { BorrowerAddressModel } from "@/models/loans";
import type { StudentFormVM } from "@/types/student";
import type { UpdateBorrowerRequest } from "@/models/loans";
import type { RootStore } from "@/types/root-store";
import type { FormChange, FormSchema, FormPatch } from "@/models/form";
import type { KeyRename, CompareOptions } from "@/types/types";
import { usePermissionsAccess } from "@/composables/usePermissionsAccess";
import { NationalIdNumberTypeIds } from "@/types/application-details";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";

const emit = defineEmits<{
  (e: "studentUpdated", personId: number): void;
}>();

const addressInputRef = ref<InstanceType<typeof AddressInput>>();
const parentAttestationConsentRef =
  ref<InstanceType<typeof ParentAttestationConsent>>();

const route = useRoute();
const router = useRouter();

const props = withDefaults(
  defineProps<{
    loanDetails: LoanDetails;
    draw?: any;
    student?: StudentFormVM;
    useApplicationLoanId?: boolean;
    permissionIds: Permissions[];
    permissionLevel: number;
  }>(),
  {
    useApplicationLoanId: false,
    permissionIds: () => [],
    permissionLevel: PermissionLevels.None,
  }
);

const permissionsAccess = usePermissionsAccess();

let person = ref({
  id: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  citizenshipStatusId: 0,
  citizenshipStatusName: "",
  nationalIdNumberTypeId: 0,
  nationalIdNumber: "",
  parentConsent: undefined as undefined | boolean,
  parentAttestation: undefined as undefined | any,
  address: {} as ApplicantAddress,
  itinIndicator: undefined as undefined | boolean,
});

//#TODO
const gettingData = ref<boolean>(true);
const mode = ref<keyof typeof FormModes>(FormModes.view);
const entityTypeId = ref<number>(ConsentServiceEntityTypeIds.Draw);
const savingChanges = ref<boolean>(false);
const showAdditionalInfo = ref<boolean>(false);
const studentBackup = ref<StudentFormVM | undefined>(undefined);
const showUnitType = ref<boolean>(false);

const {
  buildStudentFormChangesTable,
  changeMode,
  citizenshipStatusOptions,
  confirmChangesTitle,
  errorModalTitle,
  errors,
  formatDateOfBirth,
  generatePatchPath,
  generatePathOp,
  getStudentInformationFormAsync,
  handleFieldChange,
  nationalIdNumberLabel,
  schema,
  setValues,
  setFieldValue,
  values,
} = useStudentFormFields({
  studentInfo: person,
  mode,
  entityTypeId,
  useAddressInputComponent: true,
});

const canUpdate = computed<boolean>(() => {
  const permissionIdsValue = [Permissions.Draws];

  return permissionsAccess.hasPermission(
    permissionIdsValue,
    PermissionLevels.Update
  );
});

const hasErrors = computed<boolean>(
  () => errors.value && Object.keys(errors.value).length > 0
);

const addressSchema = computed<FormSchema | undefined>(
  () => addressInputRef.value?.addressSchema
);

const validAddressInput = computed<boolean>(
  () => (addressInputRef.value && !addressInputRef.value?.hasErrors) === true
);

const skipKeys = computed<string[]>(() => {
  return ["suffixTypeId", "applicationLoanId", "id", "applicantId", "userId"];
});

const keysToRename = computed<KeyRename[]>(() => {
  return [
    {
      key: "nationalIdNumber",
      rename: nationalIdNumberLabel.value || "",
    } as KeyRename,
    {
      key: "dateOfBirth",
      rename: schema?.value.dateOfBirth.label,
    } as KeyRename,
    {
      key: "citizenshipStatusTypeId",
      rename: schema.value.citizenshipStatusId.label,
    } as KeyRename,
    {
      key: "citizenshipStatusId",
      rename: schema.value.citizenshipStatusId.label,
    } as KeyRename,
    {
      key: "address1",
      rename: addressSchema.value?.address1?.label,
    } as KeyRename,
    {
      key: "address2",
      rename: addressSchema.value?.address2.label,
    } as KeyRename,
    {
      key: "city",
      rename: addressSchema.value?.city.label,
    } as KeyRename,
    {
      key: "stateName",
      rename: addressSchema.value?.stateId.label,
    } as KeyRename,
    {
      key: "postalCode",
      rename: addressSchema.value?.postalCode.label,
    } as KeyRename,
    {
      key: "countryName",
      rename: addressSchema.value?.countryId.label,
    } as KeyRename,
    {
      key: "addressUnitTypeName",
      rename: addressSchema.value?.addressUnitTypeId?.label,
    } as KeyRename,
  ];
});

const originalBorrower = computed<UpdateBorrowerRequest>(() => {
  const cleanedPostalCode = props.student?.address?.postalCode.replace(
    /[^0-9]/g,
    ""
  );
  const postalCode =
    cleanedPostalCode && cleanedPostalCode.length === 9
      ? cleanedPostalCode.slice(0, 5)
      : cleanedPostalCode;

  const postalCodeExtension =
    cleanedPostalCode && cleanedPostalCode.length === 9
      ? cleanedPostalCode.slice(-4)
      : null;

  return {
    loanId: props.useApplicationLoanId
      ? props.loanDetails.applicationLoanId || props.loanDetails.loanId
      : props.loanDetails.loanId,
    borrowerId: props.student?.id,
    firstName: props.student?.firstName?.trim(),
    lastName: props.student?.lastName?.trim(),
    dateOfBirth: props.student?.dateOfBirth + "T00:00:00",
    citizenshipStatusTypeId: props.student?.citizenshipStatusId,
    suffixTypeId: null,
    borrowerEncryptedData: {
      nationalIdNumber: props.student?.nationalIdNumber,
      nationalIdNumberTypeId: props.student?.nationalIdNumberTypeId,
    },
    borrowerAddress: {
      addressTypeId: AddressTypeIds.PERMANENT,
      addressStateId: props.student?.address?.stateId,
      addressCountryId: props.student?.address?.countryId,
      addressLineOne: props.student?.address?.address1,
      addressLineTwo: props.student?.address?.address2,
      city: props.student?.address?.city,
      postalCode: postalCode,
      postalCodeExtension: postalCodeExtension,
    },
    borrowerContact: null, // ignored
    borrowerDriversLicense: null, //ignore
  } as UpdateBorrowerRequest;
});

const updateBorrowerRequest = computed<UpdateBorrowerRequest>(() => {
  return {
    loanId: props.useApplicationLoanId
      ? props.loanDetails.applicationLoanId || props.loanDetails.loanId
      : props.loanDetails.loanId,
    borrowerId: props.student?.id,
    firstName: person.value.firstName?.trim(),
    lastName: person.value.lastName?.trim(),
    dateOfBirth: person.value.dateOfBirth + "T00:00:00",
    citizenshipStatusTypeId: Number(person.value?.citizenshipStatusId),
    suffixTypeId: null,
    borrowerEncryptedData: {
      nationalIdNumber: person.value.nationalIdNumber,
      nationalIdNumberTypeId:
        person.value.itinIndicator === true
          ? NationalIdNumberTypeIds.ITIN
          : NationalIdNumberTypeIds.SSN,
    },
    borrowerAddress: {
      addressTypeId: AddressTypeIds.PERMANENT,
      addressStateId: Number(addressInputRef.value?.values?.stateId),
      addressCountryId: Number(addressInputRef.value?.values?.countryId),
      addressLineOne: addressInputRef.value?.values?.address1,
      addressLineTwo: showUnitType.value
        ? addressInputRef.value?.formattedAddress2
        : addressInputRef.value?.values?.address2,
      city: addressInputRef.value?.address?.city,
      postalCode: addressInputRef.value?.postalCode,
      postalCodeExtension: addressInputRef.value?.postalCodeExtension,
    },
    borrowerContact: null, // ignored
    borrowerDriversLicense: null, //ignore
  } as UpdateBorrowerRequest;
});

const updatedBorrowerAddress = computed<BorrowerAddressModel>(() => {
  return {
    id: props.student?.id || 0,
    addressTypeId:
      props?.student?.address?.addressTypeId ||
      addressInputRef.value?.values?.addressTypeId ||
      0,
    addressStateId:
      addressInputRef.value?.values?.stateId ||
      props?.student?.address?.stateId ||
      0,
    addressCountryId: addressInputRef.value?.values?.countryId || 0,
    addressLineOne: addressInputRef.value?.values?.address1 || "",
    addressLineTwo: showUnitType.value
      ? addressInputRef.value?.formattedAddress2 || null
      : addressInputRef.value?.values?.address2 || null,
    city: addressInputRef.value?.values?.city || "",
    postalCode: addressInputRef.value?.postalCode || "",
    postalCodeExtension: addressInputRef.value?.postalCodeExtension || null,
  };
});

const updatedAddress = computed<ApplicantAddress>(() => {
  return {
    address1: addressInputRef.value?.values?.address1 || "",
    address2: showUnitType.value
      ? addressInputRef.value?.formattedAddress2 || null
      : addressInputRef.value?.values?.address2 || null,
    addressId: addressInputRef.value?.values?.addressId || 0,
    addressTypeId: addressInputRef.value?.values?.addressTypeId || 0,
    addressTypeName: addressInputRef.value?.values?.addressTypeName || "",
    addressUnitTypeId: addressInputRef.value?.values?.addressUnitTypeId || null,
    addressUnitTypeName:
      addressInputRef.value?.values?.addressUnitTypeName || null,
    applicantId: addressInputRef.value?.values?.applicantId || 0,
    id: addressInputRef.value?.values?.id || 0,
    city: addressInputRef.value?.values?.city || "",
    stateId: addressInputRef.value?.values?.stateId || 0,
    stateName: addressInputRef.value?.values?.stateName || "",
    postalCode: addressInputRef.value?.values?.postalCode || "",
    countryId: addressInputRef.value?.values?.countryId || 0,
    countryName: addressInputRef.value?.values?.countryName || "",
  };
});

watch(
  () => updatedAddress.value,
  (newVal) => {
    person.value.address = newVal;
    setFieldValue("address", newVal);
  },
  {
    deep: true,
  }
);
function confirmChanges() {
  // Validate that the SSN and DOB are not the same as parent
  const options = {
    programTypeId: 0,
    productTypeId: 0,
    keysToSkip: skipKeys.value,
    keysToPick: [],
    keysToRename: keysToRename.value,
    keysToFormat: [],
    fieldsToCompare: [],
  } as CompareOptions;

  const formChanges = checkStudentModelChanges(
    updateBorrowerRequest.value,
    originalBorrower.value,
    options
  );

  const modalHtml = buildStudentFormChangesTable(formChanges.changes);

  console.log("formChanges", formChanges);
  console.log("modalHtml", { modalHtml });

  if (formChanges.hasChanges) {
    functions.openModal({
      title: confirmChangesTitle.value,
      description: "Please review your changes before submitting...",
      html: modalHtml,
      buttons: [
        { title: "Cancel", onClick: () => functions.closeModal() },
        {
          title: "Save Changes",
          onClick: async () => {
            functions.setModalLoading(true);
            await updateStudentInformation(formChanges.patches);
          },
        },
      ],
    });
  } else {
    resetSchemas();
    changeMode(FormModes.view);
    savingChanges.value = false;
    functions.setModalLoading(false);
    functions.closeModal();
  }
}

function submit() {
  savingChanges.value = true;

  if (!hasErrors.value && validAddressInput.value) {
    confirmChanges();
  } else {
    savingChanges.value = false;
  }
}

function resetSchemas() {
  for (const property in schema.value) {
    schema.value[property].valid = true;
    schema.value[property].errors = [];
    schema.value[property].touched = false;
  }

  addressInputRef.value?.resetSchemas();
}

function resetForm() {
  person.value = {
    id: 0,
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    citizenshipStatusId: 0,
    citizenshipStatusName: "",
    nationalIdNumberTypeId: 0,
    nationalIdNumber: "",
    parentConsent: undefined,
    parentAttestation: {},
    address: {} as ApplicantAddress,
    itinIndicator: undefined,
  };

  studentBackup.value = JSON.parse(JSON.stringify(person.value));
  person.value = JSON.parse(JSON.stringify(studentBackup.value));
}

function discardChanges() {
  if (!props.student) {
    resetForm();
    resetSchemas();
    addressInputRef.value?.discardChanges();
    return;
  }

  person.value = JSON.parse(JSON.stringify(studentBackup.value));
  studentBackup.value = JSON.parse(JSON.stringify(person.value));

  setStudentInformation();
  resetSchemas();

  addressInputRef.value?.discardChanges();
}

function handleCancelButtonClick() {
  discardChanges();
  changeMode(FormModes.view);
}

function setStudentInformation() {
  if (props.student !== undefined) {
    studentBackup.value = JSON.parse(JSON.stringify(props.student));

    person.value = {
      id: props.student?.id || 0,
      firstName: props.student?.firstName || "",
      lastName: props.student?.lastName || "",
      dateOfBirth: props.student?.dateOfBirth || "",
      citizenshipStatusId: props.student?.citizenshipStatusId || 0,
      citizenshipStatusName: props.student?.citizenshipStatusName,
      nationalIdNumber: props.student?.nationalIdNumber,
      nationalIdNumberTypeId: props.student?.nationalIdNumberTypeId,
      parentAttestation: props.student?.parentAttestation,
      parentConsent: props.student?.parentAttestation?.consentGiven,
      address: props.student?.address,
      itinIndicator:
        props.student.nationalIdNumberTypeId === NationalIdNumberTypeIds.ITIN,
    };

    setValues(person.value);

    // Set adddress
    if (props.student?.address) {
      addressInputRef.value?.setValues(props.student?.address);
    }
  }
}

function checkStudentModelChanges(
  object1: UpdateBorrowerRequest,
  object2: UpdateBorrowerRequest,
  options: CompareOptions
) {
  options.pathPrefix = undefined;

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  let html = "";

  const patches = [] as FormPatch[];
  const changes = [] as FormChange[];

  functions.compareObjects(object1, object2).forEach((reference) => {
    let before = reference.reduce((o, key) => o[key], object2);
    let after = reference.reduce((o, key) => o[key], object1);

    const path = options?.pathPrefix
      ? options?.pathPrefix + generatePatchPath(reference)
      : generatePatchPath(reference);

    // get the last variable from the reference array
    let key = reference[reference.length - 1];

    // get the last variable from the reference array, and convert the camelCase to add spaces
    let name = reference[reference.length - 1]?.replace(
      /([a-z])([A-Z])/g,
      `$1 $2`
    );

    // if (options?.keysToSkip?.includes(key)) {
    //   return;
    // }

    let rename = options?.keysToRename?.find(
      (k: KeyRename) => k.key == key
    )?.rename;

    if (rename) {
      name = rename;
    }

    if (before === undefined || before === null || before === "") {
      before = null;
    }

    if (after === undefined || after === null || after === "") {
      after = null;
    }

    if (before === after) {
      // do not add to table if the values are the same
      return;
    }

    patches.push({
      op: generatePathOp(before, after),
      path: path,
      value: after,
    });

    if (before === null) {
      before = "N/A";
    }

    if (after === null) {
      after = "N/A";
    }

    if (key === "dateOfBirth") {
      before = formatDateOfBirth(before?.split("T")[0]);
      after = formatDateOfBirth(after?.split("T")[0]);
    }

    // handle display values
    if (key === "addressStateTypeId" || key === "addressStateId") {
      name = addressSchema.value?.stateId?.label;
      before =
        before !== "N/A" && addressInputRef.value?.statesOptions
          ? getOptionLabel(addressInputRef.value?.statesOptions, Number(before))
          : before;
      after =
        after !== "N/A" && addressInputRef.value?.statesOptions
          ? getOptionLabel(addressInputRef.value?.statesOptions, Number(after))
          : after;
    }

    if (key === "addressCountryTypeId" || key === "addressCountryId") {
      name = addressSchema.value?.countryId?.label;
      before =
        before !== "N/A" && addressInputRef.value?.countryOptions
          ? getOptionLabel(
              addressInputRef.value?.countryOptions,
              Number(before)
            )
          : before;
      after =
        after !== "N/A" && addressInputRef.value?.countryOptions
          ? getOptionLabel(addressInputRef.value?.countryOptions, Number(after))
          : after;
    }

    if (key === "addressLineOne") {
      name = addressSchema.value?.address1?.label;
    }

    if (key === "addressLineTwo") {
      name = addressSchema.value?.address2?.label;
    }

    if (key === "city") {
      name = addressSchema.value?.city?.label;
    }

    if (key === "citizenshipStatusTypeId") {
      name = schema.value?.citizenshipStatusId?.label;
      before =
        before !== "N/A" && citizenshipStatusOptions.value
          ? getOptionLabel(citizenshipStatusOptions.value, Number(before))
          : before;
      after =
        after !== "N/A" && citizenshipStatusOptions.value
          ? getOptionLabel(citizenshipStatusOptions.value, Number(after))
          : after;
    }

    if (!options?.keysToSkip?.includes(key)) {
      changes.push({
        key: key,
        name: name,
        label: name,
        before: before,
        after: after,
        value: patches.find((p) => p.path)?.value,
        comment: `${functions.capitalize(name)} : ${after}`,
      });

      html += `<tr><td class="xbefore">${name}</td><td class="xafter">${before}</td><td class="xafter">${after}</td></tr>`;
    }
  });

  const filteredChanges = changes.filter(
    (change) =>
      !(options?.keysToExcludeFromSummaryComment || []).includes(change.key)
  );

  const lastIndex = filteredChanges.length - 1;

  const changeSummaries = filteredChanges
    .map((change, index) => {
      if (index === lastIndex) {
        return change.comment + ".";
      } else if (index === 0) {
        return change.comment + " | ";
      } else {
        return change.comment + " " + " | ";
      }
    })
    .join("");

  const fieldsChanged = filteredChanges
    .map((change, index) => {
      if (filteredChanges.length === 1) {
        return change.name;
      } else if (filteredChanges.length === 2) {
        return index === lastIndex ? change.name : `${change.name} and `;
      } else {
        if (index === lastIndex) {
          return `and ${change.name}`;
        } else {
          return change.name + ", ";
        }
      }
    })
    .join("");

  const errorMessage = `${fieldsChanged} change${
    filteredChanges.length > 1 ? "s" : ""
  } could not be saved.`;

  const comment = `${options?.commentPrefix} ${changeSummaries}`;

  return {
    patches,
    changes,
    html,
    hasChanges: patches.length > 0,
    errorMessage,
    comment,
  };
}

async function updateStudentInformation(operations: FormPatch[]) {
  // filter out nationalIdnumber if it's the masked value
  // Follows XXX-XX-#### format
  const masked = /^X{3}-X{2}-\d{4}$/.test(person.value?.nationalIdNumber);
  let request = {
    ...updateBorrowerRequest.value,
  } as UpdateBorrowerRequest;

  if (masked) {
    request.borrowerEncryptedData = {
      nationalIdNumber: null,
      nationalIdNumberTypeId:
        updateBorrowerRequest.value.borrowerEncryptedData
          ?.nationalIdNumberTypeId || null,
    };

    operations = operations.filter(
      (o) => o.path !== "/student/nationalIdNumber"
    );
  }
  console.log("patch ops", operations);

  const response = await $api.loans.updateBorrowerAsync(
    props.loanDetails.loan.id,
    request
  );

  if (!response?.errors) {
    emit("studentUpdated", person.value?.id || 0);
    savingChanges.value = false;
  } else {
    savingChanges.value = false;
    functions.openModal({
      title: "Error",
      description: "There was an issue saving the Student",
    });
  }
}

async function initializeForm(): Promise<void> {
  gettingData.value = true;

  await getStudentInformationFormAsync(route.params.referenceId.toString());

  setStudentInformation();

  gettingData.value = false;
}

function toggleQuery(
  queryKey: string,
  flag?: boolean,
  shouldDelete: undefined | boolean = false
) {
  // 1. Create a shallow copy of the existing query parameters
  const query = { ...route.query };

  // 2. Toggle specific query key
  if (shouldDelete && query[queryKey]) {
    delete query[queryKey]; // Removes the key from the object
  } else {
    query[queryKey] = `${flag}`; // Adds the key to the object or updates it
  }

  // 3. Update the route without triggering a full page refresh and preserve hash
  router.replace({
    query: query,
    hash: route.hash, // Preserves the current hash (e.g., #section-2)
  });
}

watch(
  () => mode.value,
  () => {
    if (mode.value === FormModes.edit) {
      showAdditionalInfo.value = true;
    } else {
      for (const property in schema.value) {
        schema.value[property].valid = true;
        schema.value[property].errors = [];
        schema.value[property].touched = false;
      }
    }
  }
);

// watch for the modal to close
const confirmChangesModalActive = computed<boolean>(
  () =>
    (store.state as RootStore).modal.active &&
    (store.state as RootStore).modal.title === confirmChangesTitle.value
);

const errorModalActive = computed<boolean>(
  () =>
    (store.state as RootStore).modal.active &&
    (store.state as RootStore).modal.title === errorModalTitle.value
);

const StudentDetailsExpandedQueryParam = computed(
  () => route.query.studentDetailsExpanded
);

watch(
  () => showAdditionalInfo.value,
  () => {
    toggleQuery("studentDetailsExpanded", showAdditionalInfo.value);
  }
);

watch(
  () => confirmChangesModalActive.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      savingChanges.value = false;
    }
  }
);

watch(
  () => errorModalActive.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      savingChanges.value = false;
    }
  }
);
watch(
  () => props.student,
  async (newVal, oldVal) => {
    console.log("student changed", { oldVal, newVal });

    await initializeForm();
  },
  {
    deep: true,
  }
);
onMounted(async () => {
  await initializeForm();

  if (StudentDetailsExpandedQueryParam.value) {
    showAdditionalInfo.value =
      StudentDetailsExpandedQueryParam.value === "true" ? true : false;
  }
});

// watch for changes to the param and get latest student info form
watch(
  () => route.params.referenceId,
  async () => {
    await initializeForm();
  }
);

defineExpose({
  addressInputRef,
  changeMode,
  parentAttestationConsentRef,
  updateBorrowerRequest,
  updatedAddress,
  updatedBorrowerAddress,
  values,
});
</script>

<style src="@/assets/css/student-information-form.css" scoped></style>

<style scoped>
.xform-section {
  border: none;
  margin-top: 0;
  padding-top: 0;
}
.container {
  display: block;
  position: relative;
}
.toggle {
  cursor: pointer;
  text-align: center;
  margin-bottom: 30px;
}
.submit {
  margin-bottom: 30px;
}
.submit-button {
  position: absolute;
  right: 1rem;
  margin-bottom: 30px;
}
.container h1 {
  width: 100%;
}

button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: "Montserrat", Arial, sans-serif;
  transition: background-color 0.2s, color 0.2s;
  font-size: 14px;
  gap: 8px;
  min-width: 98px;
  font-weight: 400;
}

div.xfield {
  margin-bottom: 0 !important;
}

@media screen and (max-width: 800px) {
  .toggle {
    margin-top: 20px;
  }
}
</style>
